import { useState } from "react";
import { CheckCircle2, RotateCcw, Save, X } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { inputCls, money } from "./_shared";

const today = () => new Date().toISOString().slice(0, 10);

export function FilingForm({ period, onSaved, onClose }) {
  const f = period.filing || {};
  const [form, setForm] = useState({
    status: f.status && f.status !== "to_file" ? f.status : "filed",
    filed_at: f.filed_at || today(),
    paid_at: f.paid_at || today(),
    paid_amount: f.paid_amount ?? period.net_total,
    confirmation_number: f.confirmation_number || "",
    note: f.note || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, paid_amount: form.paid_amount === "" ? null : Number(form.paid_amount) };
      const r = await api.put(`/admin/cabinet-taxes/periods/${period.key}/filing`, payload);
      toast.success(form.status === "paid" ? "Période marquée payée" : "Période marquée produite");
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Remettre cette période « À produire » ?")) return;
    try {
      await api.delete(`/admin/cabinet-taxes/periods/${period.key}/filing`);
      toast.success("Statut réinitialisé");
      onSaved(null);
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3" data-testid="filing-form">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-indigo-900 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Statut de la déclaration — {period.label}</div>
        <button onClick={onClose} className="text-indigo-700 hover:text-indigo-900" data-testid="filing-form-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-2">
        {[["filed", "Produite"], ["paid", "Payée"]].map(([v, l]) => (
          <button key={v} onClick={() => setForm({ ...form, status: v })}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${form.status === v ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-indigo-800 border-indigo-200 hover:border-indigo-400"}`}
            data-testid={`filing-status-${v}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-xs text-gray-600">Date de production
          <input type="date" value={form.filed_at} onChange={(e) => setForm({ ...form, filed_at: e.target.value })} className={`${inputCls} mt-1`} data-testid="filing-filed-at" />
        </label>
        {form.status === "paid" && (
          <>
            <label className="text-xs text-gray-600">Date du paiement
              <input type="date" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} className={`${inputCls} mt-1`} data-testid="filing-paid-at" />
            </label>
            <label className="text-xs text-gray-600">Montant remis (calculé : {money(period.net_total)})
              <input type="number" step="0.01" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} className={`${inputCls} mt-1`} data-testid="filing-paid-amount" />
            </label>
          </>
        )}
        <label className="text-xs text-gray-600">N° de confirmation
          <input type="text" value={form.confirmation_number} onChange={(e) => setForm({ ...form, confirmation_number: e.target.value })} maxLength={60} placeholder="Revenu Québec / Mon dossier" className={`${inputCls} mt-1`} data-testid="filing-confirmation" />
        </label>
        <label className="text-xs text-gray-600 sm:col-span-3">Note
          <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={1000} className={`${inputCls} mt-1`} data-testid="filing-note" />
        </label>
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        {f.status && f.status !== "to_file" ? (
          <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800" data-testid="filing-reset">
            <RotateCcw className="w-3.5 h-3.5" /> Remettre « À produire »
          </button>
        ) : <span />}
        <button onClick={submit} disabled={saving}
          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
          data-testid="filing-save">
          <Save className="w-4 h-4" /> Enregistrer
        </button>
      </div>
    </div>
  );
}
